import { z } from "zod";
import { addLocalDays, amsterdamDate, amsterdamParts, fromAmsterdam } from "./amsterdam.js";
import { SUBSCRIPTION_TOPICS } from "./helpcenter.js";

/** Statuspagina (docs/HELPCENTER.md §7): vaste onderdelen, storingen en onderhoud. */

export const STATUS_COMPONENTS = [
  { key: "web", name: "Webapp en API" },
  { key: "login", name: "Inloggen (Google en inloglink)" },
  { key: "files", name: "Bestanden en foto's" },
  { key: "mail", name: "E-mailmeldingen" },
  { key: "realtime", name: "Realtime updates" },
  { key: "sync", name: "Synchronisatie na offline werken" },
] as const;
export type StatusComponentKey = (typeof STATUS_COMPONENTS)[number]["key"];

export const COMPONENT_STATUSES = ["operational", "degraded", "outage", "maintenance"] as const;
export type ComponentStatus = (typeof COMPONENT_STATUSES)[number];
export const COMPONENT_STATUS_LABELS: Record<ComponentStatus, string> = {
  operational: "Werkt",
  degraded: "Verstoring",
  outage: "Storing",
  maintenance: "Onderhoud",
};
/** Totaal bovenaan de statuspagina. */
export const OVERALL_STATUS_LABELS: Record<ComponentStatus, string> = {
  operational: "Alle systemen werken",
  degraded: "Gedeeltelijke verstoring",
  outage: "Storing",
  maintenance: "Onderhoud",
};

const RANK: Record<ComponentStatus, number> = { operational: 0, maintenance: 1, degraded: 2, outage: 3 };

/** Ergste status: storing gaat voor verstoring, dat voor onderhoud, dat voor werkt. */
export function worstComponentStatus(statuses: Iterable<ComponentStatus>): ComponentStatus {
  let worst: ComponentStatus = "operational";
  for (const s of statuses) if (RANK[s] > RANK[worst]) worst = s;
  return worst;
}

export const INCIDENT_KINDS = ["incident", "maintenance"] as const;
export type IncidentKind = (typeof INCIDENT_KINDS)[number];
export const INCIDENT_KIND_LABELS: Record<IncidentKind, string> = { incident: "Storing", maintenance: "Onderhoud" };

export const INCIDENT_SEVERITIES = ["degraded", "outage"] as const;
export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number];

export const INCIDENT_PHASES = ["investigating", "identified", "monitoring", "resolved"] as const;
export type IncidentPhase = (typeof INCIDENT_PHASES)[number];
export const INCIDENT_PHASE_LABELS: Record<IncidentPhase, string> = {
  investigating: "Melding",
  identified: "Oorzaak gevonden",
  monitoring: "Wordt opgelost",
  resolved: "Opgelost",
};

/** Een periode met een status, voor de beschikbaarheid en de 90-dagenweergave. */
export interface StatusSpan {
  status: ComponentStatus;
  start: Date;
  /** null = loopt nog */
  end: Date | null;
}

/** Status die een melding aan een onderdeel geeft. */
export const incidentStatus = (kind: IncidentKind, severity: IncidentSeverity | null): ComponentStatus =>
  kind === "maintenance" ? "maintenance" : (severity ?? "degraded");

/** Minuten in [from, to) die door minstens één van de periodes worden bedekt (overlap één keer). */
function coveredMs(spans: { start: number; end: number }[], from: number, to: number): number {
  const clipped = spans
    .map((s) => ({ start: Math.max(s.start, from), end: Math.min(s.end, to) }))
    .filter((s) => s.end > s.start)
    .sort((a, b) => a.start - b.start);
  let total = 0;
  let curStart = -Infinity;
  let curEnd = -Infinity;
  for (const s of clipped) {
    if (s.start > curEnd) {
      if (curEnd > curStart) total += curEnd - curStart;
      curStart = s.start;
      curEnd = s.end;
    } else curEnd = Math.max(curEnd, s.end);
  }
  if (curEnd > curStart) total += curEnd - curStart;
  return total;
}

/**
 * Beschikbaarheid in procenten, afgerond op twee decimalen: tijd zonder verstoring of storing gedeeld
 * door de tijd in de periode. Onderhoud telt niet mee als onbeschikbaar.
 */
export function uptime(spans: StatusSpan[], from: Date, to: Date): number {
  const total = to.getTime() - from.getTime();
  if (total <= 0) return 100;
  const down = coveredMs(
    spans
      .filter((s) => s.status === "degraded" || s.status === "outage")
      .map((s) => ({ start: s.start.getTime(), end: (s.end ?? to).getTime() })),
    from.getTime(),
    to.getTime(),
  );
  return Math.round(((total - down) / total) * 10_000) / 100;
}

export interface DayStatus {
  /** YYYY-MM-DD (Amsterdam) */
  date: string;
  status: ComponentStatus;
}

/** Per dag (Amsterdam) de ergste status, de laatste `days` dagen tot en met vandaag. */
export function dailyStatuses(spans: StatusSpan[], now: Date, days = 90): DayStatus[] {
  const p = amsterdamParts(now);
  const todayStart = fromAmsterdam(p.year, p.month, p.day);
  const out: DayStatus[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const start = addLocalDays(todayStart, -i);
    const end = addLocalDays(start, 1);
    const hits = spans.filter((s) => s.start < end && (s.end ?? now) > start).map((s) => s.status);
    out.push({ date: amsterdamDate(start), status: worstComponentStatus(hits) });
  }
  return out;
}

// ---------- API ----------

export type IncidentState = "scheduled" | "active" | "resolved";
export const INCIDENT_STATE_LABELS: Record<IncidentState, string> = {
  scheduled: "Gepland",
  active: "Loopt",
  resolved: "Opgelost",
};

export interface IncidentUpdateDto {
  id: string;
  phase: IncidentPhase;
  body: string;
  createdAt: string;
}
export interface IncidentDto {
  id: string;
  kind: IncidentKind;
  title: string;
  severity: IncidentSeverity | null;
  state: IncidentState;
  /** Status die de melding de onderdelen geeft. */
  status: ComponentStatus;
  startedAt: string | null;
  resolvedAt: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  showBanner: boolean;
  components: StatusComponentKey[];
  updates: IncidentUpdateDto[];
}
export interface StatusComponentDto {
  id: string;
  key: StatusComponentKey;
  name: string;
  /** Huidige status: de ergste van de handmatige status en lopende meldingen. */
  status: ComponentStatus;
  /** Handmatig gezet door support. */
  manual: ComponentStatus;
  uptime90: number;
  days: DayStatus[];
}
export interface StatusResponse {
  generatedAt: string;
  overall: ComponentStatus;
  components: StatusComponentDto[];
  active: IncidentDto[];
  scheduled: IncidentDto[];
  /** Opgeloste meldingen van de laatste 90 dagen, nieuwste eerst. */
  past: IncidentDto[];
}
export interface StatusBannerResponse {
  overall: ComponentStatus;
  banners: {
    id: string;
    kind: IncidentKind;
    title: string;
    state: IncidentState;
    status: ComponentStatus;
    scheduledStart: string | null;
  }[];
  /** Statuspagina op de helpsite, als die is ingesteld. */
  statusUrl: string | null;
}

const isoDate = z.iso.datetime({ offset: true });
export const incidentUpdateBodySchema = z.object({
  phase: z.enum(INCIDENT_PHASES),
  body: z.string().trim().min(1, "Vul een tekst in").max(5000),
  /** Bevestigde volgers mailen. */
  notify: z.boolean().default(false),
});
export const incidentBodySchema = z
  .object({
    kind: z.enum(INCIDENT_KINDS),
    title: z.string().trim().min(1, "Vul een titel in").max(160),
    severity: z.enum(INCIDENT_SEVERITIES).nullable(),
    components: z
      .array(z.enum(STATUS_COMPONENTS.map((c) => c.key) as [StatusComponentKey, ...StatusComponentKey[]]))
      .min(1, "Kies minstens één onderdeel"),
    showBanner: z.boolean(),
    scheduledStart: isoDate.nullable().default(null),
    scheduledEnd: isoDate.nullable().default(null),
  })
  .refine((b) => b.kind === "maintenance" || b.severity !== null, { message: "Kies de ernst", path: ["severity"] })
  .refine((b) => b.kind === "incident" || (b.scheduledStart !== null && b.scheduledEnd !== null), {
    message: "Vul begin en einde van het onderhoud in",
    path: ["scheduledStart"],
  })
  .refine((b) => !b.scheduledStart || !b.scheduledEnd || b.scheduledEnd > b.scheduledStart, {
    message: "Het einde moet na het begin liggen",
    path: ["scheduledEnd"],
  });
export type IncidentBody = z.infer<typeof incidentBodySchema>;
/** Nieuwe melding: de gegevens plus de eerste update. */
export const newIncidentBodySchema = z.object({ incident: incidentBodySchema, update: incidentUpdateBodySchema });
export const componentStatusBodySchema = z.object({ status: z.enum(COMPONENT_STATUSES) });

export const subscribeBodySchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email("Ongeldig e-mailadres")),
  topic: z.enum(SUBSCRIPTION_TOPICS),
});

/** Gepland onderhoud minimaal 3 werkdagen vooraf aankondigen (§7.3). */
export const MAINTENANCE_NOTICE_WORKDAYS = 3;
